// Pure helpers over the flat `nodes` array (parent-pointer + order fields),
// shared by the hierarchy panel (drag-reparent/reorder) and the editor's
// scene-graph builder.
import { nanoid } from "nanoid";
import { cloneDeep } from "lodash";

// Sentinel `selectedNodeId` for the fixed "World" hierarchy row — scene-wide
// settings (ambient light, world AI description) aren't a real node, so this
// never appears in the `nodes` array. Safe as a literal: nanoid() ids are
// always 21 characters, so a real node can never collide with it.
export const WORLD_NODE_ID = "world";

export function childrenOf(nodes, parentId) {
  return nodes
    .filter((n) => (n.parent || null) === (parentId || null))
    .sort((a, b) => a.order - b.order);
}

// Walk up from `nodeId`'s parent chain — true if `ancestorId` is among them.
// Used to reject drops that would reparent a node under its own descendant.
export function isDescendantOf(nodes, nodeId, ancestorId) {
  const byId = new Map(nodes.map((n) => [n._id, n]));
  let current = byId.get(nodeId);
  while (current?.parent) {
    if (current.parent === ancestorId) return true;
    current = byId.get(current.parent);
  }
  return false;
}

// Every node in `nodeId`'s subtree, at any depth (not just direct children).
export function descendantsOf(nodes, nodeId) {
  return childrenOf(nodes, nodeId).flatMap((child) => [
    child,
    ...descendantsOf(nodes, child._id),
  ]);
}

// Deep-clones a flat subtree (root first — e.g. `[node, ...descendantsOf(nodes,
// node._id)]`, captured at copy time by Scene3DEditor.jsx's copySelected) with
// fresh ids, remapping every internal `parent` pointer so descendants still
// point at their (also freshly cloned) parent. The root's own `parent` is left
// as-is — it points outside the subtree, and reorderNode overwrites it to
// wherever the caller is actually pasting. Nodes never reference each other by
// id except via `parent`, so nothing else needs remapping. Cloning here, at
// paste time rather than copy time, is what lets one copy be pasted repeatedly
// without every paste colliding on the same ids or sharing nested objects
// (transform, etc.) with one another.
export function cloneSubtree(subtreeNodes) {
  const idMap = new Map(subtreeNodes.map((n) => [n._id, nanoid()]));
  return cloneDeep(subtreeNodes).map((n) => ({
    ...n,
    _id: idMap.get(n._id),
    parent: idMap.get(n.parent) ?? n.parent,
  }));
}

// Moves `draggedId` to be a child of `newParentId` (null = root) at
// `newIndex` among its new siblings, renumbering just that sibling group's
// `order` fields. Other siblings (e.g. the ones left behind at the old
// parent) keep their existing order values — gaps there are harmless since
// `order` is only ever compared within a single parent's children.
export function reorderNode(nodes, draggedId, newParentId, newIndex) {
  const draggedNode = nodes.find((n) => n._id === draggedId);
  if (!draggedNode) return nodes;

  const siblings = childrenOf(nodes, newParentId).filter(
    (n) => n._id !== draggedId,
  );
  const clampedIndex = Math.max(0, Math.min(newIndex, siblings.length));
  const newSiblingOrder = [
    ...siblings.slice(0, clampedIndex),
    draggedNode,
    ...siblings.slice(clampedIndex),
  ];
  const orderById = new Map(newSiblingOrder.map((n, i) => [n._id, i]));

  return nodes.map((n) => {
    if (n._id === draggedId) {
      return { ...n, parent: newParentId || null, order: orderById.get(n._id) };
    }
    if (orderById.has(n._id)) {
      return { ...n, order: orderById.get(n._id) };
    }
    return n;
  });
}
